<template>
  <div>
    <div class="card">
      <div class="card-header">
        <h4 class="card-title">Общая информация</h4>
      </div>
      <div class="card-body">
        <span>Дата создания альбома: {{ formatDate(date) }}</span>
      </div>
      <div class="card-body">
        <b-form @submit.prevent>
          <b-row>
            <b-col md="6">
              <b-form-group label="Название старта">
                <v-select
                  v-model="startOption"
                  :placeholder="'Введите название старта'"
                  :close-on-select="true"
                  :options="startOptions"
                  :reduce="startOption => startOption"
                  label="label"
                  input-id="short_name"
                  :value="startOption"
                  class="select"
                  @search="(search, loading) => getStarts(search, loading)"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </b-form>
      </div>

      <div class="card-body">
        <b-form @submit.prevent>
          <b-row>
            <b-col md="6">
              <b-form-group label="Название альбома">
                <div class="flex-row">
                  <b-form-input
                    v-model="albumName"
                    placeholder="Введите название альбома"
                  />
                </div>
              </b-form-group>
            </b-col>
          </b-row>
        </b-form>
      </div>

      <div class="card-body">
        <b-form @submit.prevent>
          <b-row>
            <b-col md="6">
              <div class="flex-row">
                <b-form-checkbox
                  v-model="isBeforeStart"
                  type="checkbox"
                />
                <span>Альбом будет показан до начала старта</span>
              </div>
            </b-col>
          </b-row>
        </b-form>
      </div>
    </div>

    <div class="card">
      <div class="card-header">
        <h4 class="card-title">Фотографии</h4>
      </div>
      <div class="card-body">
        <b-col
          md="6"
          style="padding-left: 0"
        >
          <b-tabs content-class="mt-1">
            <b-tab
              title="Обычная загрузка"
              active
            >
              <div class="upload-wrap">
                <span>Загрузить фото</span>
                <multi-file-uploader
                  v-model="newFiles"
                  @updatePhotos="getPhotos"
                  @checkNewPhotos="checkNewPhotos"
                />
              </div>
            </b-tab>

            <b-tab title="Пакетная загрузка">
              <div
                class="batch-dropzone"
                :class="{ 'is-dragover': batchDragOver }"
                @dragenter.prevent="batchDragOver = true"
                @dragover.prevent="batchDragOver = true"
                @dragleave.prevent="batchDragOver = false"
                @drop.prevent="onBatchDrop"
              >
                <div class="batch-dropzone__title">
                  Перетащите сюда фото или выберите файлы
                </div>

                <div class="batch-dropzone__actions">
                  <b-form-file
                    multiple
                    accept="image/*"
                    browse-text="Выбрать"
                    placeholder="Файлы не выбраны"
                    @change="onBatchPick"
                  />
                </div>

                <div
                  v-if="batchFiles.length"
                  class="batch-list"
                >
                  <div class="batch-list__header">
                    <div>
                      Выбрано: <b>{{ batchFiles.length }}</b>
                    </div>
                    <div class="batch-list__buttons">
                      <b-button
                        size="sm"
                        variant="outline-secondary"
                        @click="clearBatch"
                        :disabled="batchUploading"
                      >
                        Очистить
                      </b-button>
                      <b-button
                        size="sm"
                        variant="primary"
                        @click="uploadBatch"
                        :disabled="batchUploading || !batchFiles.length"
                      >
                        Загрузить {{ batchFiles.length }}
                      </b-button>
                    </div>
                  </div>

                  <div class="batch-list__items">
                    <div
                      v-for="(f, i) in batchFiles"
                      :key="f.name + f.lastModified + i"
                      class="batch-item"
                    >
                      <img
                        class="batch-item__preview"
                        :src="f.__preview"
                        alt=""
                      />
                      <div class="batch-item__meta">
                        <div class="batch-item__name">{{ f.name }}</div>
                        <div class="batch-item__sub">
                          {{ prettyBytes(f.size) }} • lastModified:
                          {{ f.lastModified }}
                        </div>
                      </div>
                      <b-button
                        size="sm"
                        variant="outline-danger"
                        @click="removeBatchFile(i)"
                        :disabled="batchUploading"
                      >
                        Удалить
                      </b-button>
                    </div>
                  </div>

                  <div
                    v-if="batchUploading"
                    class="mt-1"
                  >
                    <b-progress
                      :value="batchProgress"
                      max="100"
                      height="10px"
                    />
                    <div class="batch-progress-text">{{ batchProgress }}%</div>
                  </div>

                  <div
                    v-if="batchError"
                    class="batch-error"
                  >
                    {{ batchError }}
                  </div>
                </div>
              </div>
            </b-tab>
          </b-tabs>
        </b-col>
        <vue-selecto
          ref="selecto"
          drag-container=".images-container"
          :selectable-targets="['.selecto-area .card__wrapper']"
          :hit-rate="20"
          :select-by-click="true"
          :scroll-options="scrollOptions"
          :ratio="0"
          @dragStart="onDragStart"
          @select="onSelect"
          @scroll="onScroll"
        />
        <div
          id="selecto1"
          ref="scroller"
          :class="[
            'images-container',
            'scroll',
            'selecto-area',
            photos.length ? '' : 'hidden-container',
          ]"
          @scroll="onScrollerScroll"
        >
          <div
            v-for="(photo, index) in photos"
            :key="index"
            style="display: flex; flex-direction: column"
            class="photo-container"
          >
            <photo-item
              :album="album"
              :photo="photo"
              @setSelectedPhoto="setSelectedPhoto"
              @deletePhoto="deletePhoto"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="card">
      <div class="card-header">
        <h4 class="card-title">Теги для выбранных фотографий</h4>
      </div>
      <div class="card-body">
        <photo-archive-tag-panel
          :start-id="startOption.id"
          :album-id="album.id"
          :checked-photos="checkedPhotos"
          @tags-applied="onTagsApplied"
        />
      </div>
    </div>

    <b-col>
      <b-button
        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
        type="submit"
        variant="primary"
        class="mr-1 save-button"
        :disabled="isloading"
        @click="saveItem"
      >
        <b-spinner
          v-if="isloading"
          small
        />
        {{ isloading ? 'Сохранение...' : 'Сохранить' }}
      </b-button>
    </b-col>
  </div>
</template>

<script>
import {
  BButton,
  BCol,
  BForm,
  BFormGroup,
  BFormInput,
  BRow,
  BSpinner,
  VBPopover,
  BFormCheckbox,
  BTabs,
  BTab,
  BProgress,
  BFormFile,
} from 'bootstrap-vue'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import 'quill/dist/quill.bubble.css'
import Ripple from 'vue-ripple-directive'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import vSelect from 'vue-select'
import { VueSelecto } from 'vue-selecto'
import MultiFileUploader from '@/global-components/MultiFileUploader'
import crudModule from '../../../libs/crud_module'
import PhotoItem from './Photo.vue'
import PhotoArchiveTagPanel from './PhotoArchiveTagPanel.vue'
// the app's axios instance: it signs the request and renews the session (the file routes require sign-in)
import axios from '@axios'
import config from '@core/app-config/api-config'

const albumModule = crudModule({
  entity_name: 'album',
})
const startModule = crudModule({
  entity_name: 'start',
})
const albumPhotoModule = crudModule({
  entity_name: 'album/photo',
})

export default {
  name: 'AlbumPage',
  components: {
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BForm,
    BButton,
    BSpinner,
    BFormCheckbox,
    ToastificationContent,
    MultiFileUploader,
    vSelect,
    VueSelecto,
    PhotoItem,
    PhotoArchiveTagPanel,
    BTabs,
    BTab,
    BProgress,
    BFormFile,
  },
  directives: {
    'b-popover': VBPopover,
    Ripple,
  },
  data() {
    return {
      isloading: false,
      file: {},
      newFiles: null,
      photoIds: [],
      date: null,
      startOptions: [],
      startOption: {},
      photos: [],
      album: {},
      checkedPhotos: [],
      albumName: '',
      newPhotos: [],
      newPhotosIds: [],
      isBeforeStart: false,
      scrollOptions: {},
      filesBeforeUpload: false,
      startData: null,
      batchFiles: [],
      batchUploading: false,
      batchProgress: 0,
      batchError: null,
      batchDragOver: false,
      batchChunkSize: 20,
    }
  },
  computed: {
    startName() {
      if (this.album.start) {
        return this.album.start.short_name
      }
      return ''
    },
    fullPath(file) {
      if (file.fullPath) {
        return file.fullPath
      }
      return ''
    },
  },
  mounted() {
    this.scrollOptions = {
      container: this.$refs.scroller,
      throttleTime: 30,
      threshold: 0,
    }
  },
  async created() {
    try {
      await albumModule
        .fetchItem(this.$route.params.id, {
          query: {
            admin: '1',
          },
        })
        .then(async result => {
          this.album = result.data
          this.startData = null
          if (!result.data.start.short_name) {
            await startModule.fetchItem(result.data.start_id).then(res => {
              this.startData = res.data
            })
          }
          this.startOption = {
            ...result.data.start,
            short_name: this.startData
              ? this.startData.name
              : result.data.start.short_name,
          }
          this.startOption.label = `${this.startOption.short_name} (${this.formatDate(this.startOption.start_date)})`
          this.date = result.data.createdAt
          this.photos = result.data.photos
          this.albumName = result.data.name
          this.isBeforeStart = result.data.isBeforeStart
          this.setPhotoIds(result.data.photos)
        })
    } catch (e) {
      console.error(e)
    }
  },
  methods: {
    onTagsApplied() {
      this.checkedPhotos = []
      document.querySelectorAll('.photo-container.active').forEach(el => {
        el.classList.remove('active')
      })
    },
    onDragStart(e) {
      if (e.inputEvent.target.nodeName === 'BUTTON') {
        return false
      }
      return true
    },
    onSelect(e) {
      e.added.forEach(el => {
        el.classList.add('active')
        this.setSelectedPhoto(+el.id, [true])
      })
      e.removed.forEach(el => {
        el.classList.remove('active')
        this.setSelectedPhoto(+el.id, [false])
      })
    },
    onScroll(e) {
      this.$refs.scroller.scrollBy(e.direction[0] * 10, e.direction[1] * 10)
    },
    resetScroll() {
      this.$refs.scroller.scrollTo(0, 0)
    },
    onScrollerScroll() {
      this.$refs.selecto.checkScroll()
    },

    // соберём список всех file_id уже существующих фото
    setPhotoIds(photos) {
      this.photoIds = photos
        .map(item => item.file_id || item.file?.id)
        .filter(Boolean)
    },

    setSelectedPhoto(value, checked) {
      if (checked[0]) {
        const photo = this.photos.find(item => item.file_id === value)
        if (photo && !this.checkedPhotos.some(item => item.file_id === value)) {
          this.checkedPhotos.push(photo)
        }
      } else {
        const index = this.checkedPhotos.findIndex(
          photo => photo.file_id === value,
        )
        if (index > -1) {
          this.checkedPhotos.splice(index, 1)
        }
      }
    },

    saveItem() {
      this.updateAlbum()
    },

    async updateAlbum() {
      try {
        const payload = {
          start_id: this.startOption.id,
          name: this.albumName,
          isBeforeStart: this.isBeforeStart,
        }

        await albumModule
          .updateItem(this.album.id, payload)
          .then(response => {
            this.isloading = false
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Уведомление',
                icon: 'BellIcon',
                text: 'Альбом сохранен',
                variant: 'success',
              },
            })
            this.$router.push({ name: 'albums' })
            return response
          })
          .catch(error => {
            this.isloading = false
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Уведомление',
                icon: 'BellIcon',
                text: error.response.data.message,
                variant: 'danger',
              },
            })
          })
      } catch (e) {
        this.isloading = false
        console.error(e)
      }
    },

    getPhotos(data) {
      const fileId = data.id

      // если такое фото уже есть в альбоме — вообще ничего не делаем
      if (this.photoIds.includes(fileId)) {
        return
      }

      // добавляем в список известных id
      this.photoIds.push(fileId)

      const fileForPhoto = { file: data }
      this.newPhotos.push(fileForPhoto)
      this.photos.push(fileForPhoto)

      // отправляем на сервер только один конкретный id
      this.newPhotosIds = [fileId]

      this.postPhoto()
    },

    async postPhoto() {
      if (!this.newPhotosIds.length) {
        return
      }

      const postPhotoModule = crudModule({
        entity_name: `album/photo/${this.album.id}`,
      })

      const payload = this.newPhotosIds.map(photoId => ({ file_id: photoId }))

      try {
        await postPhotoModule
          .createItem(payload)
          .then(result => {
            this.album = result.data
            this.startOption = result.data.start
            this.photos = result.data.photos
            this.albumName = result.data.name
            this.setPhotoIds(result.data.photos)
            this.newPhotosIds = []
            return result
          })
          .catch(error => {
            this.isloading = false
          })
      } catch (e) {
        this.isloading = false
        console.error(e)
      }
    },

    async deletePhoto(photo) {
      if (photo.id) {
        try {
          await albumPhotoModule
            .deleteItem(photo.id)
            .then(response => {
              this.isloading = false
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: 'Уведомление',
                  icon: 'BellIcon',
                  text: 'Фотография удалена',
                  variant: 'success',
                },
              })
              const index = this.checkedPhotos.findIndex(
                id => id === photo.file.id,
              )
              if (index > -1) {
                this.checkedPhotos.splice(index, 1)
              }
              this.photos = this.photos.filter(
                item => item.file.id !== photo.file.id,
              )
              this.photoIds = this.photoIds.filter(
                item => item !== photo.file.id,
              )

              return response
            })
            .catch(error => {
              this.isloading = false
              this.$toast({
                component: ToastificationContent,
                props: {
                  title: 'Уведомление',
                  icon: 'BellIcon',
                  text: error.response.data.message,
                  variant: 'danger',
                },
              })
            })
        } catch (e) {
          this.isloading = false
          console.error(e)
        }
      } else {
        const index = this.checkedPhotos.findIndex(id => id === photo.file.id)
        if (index > -1) {
          this.checkedPhotos.splice(index, 1)
        }
        this.photos = this.photos.filter(item => item.file.id !== photo.file.id)
        this.photoIds = this.photoIds.filter(item => item !== photo.file.id)
      }
    },

    formatDate(date) {
      if (date) {
        const newDate = new Date(date)
        const options = { year: 'numeric', month: 'long', day: 'numeric' }
        const newStr = newDate.toLocaleDateString('ru', options)
        return newStr
      }
      return ''
    },

    checkNewPhotos(data) {
      this.filesBeforeUpload = data.length !== 0
    },
    async postPhotoBatch(fileIds) {
      if (!fileIds?.length) return

      const postPhotoModule = crudModule({
        entity_name: `album/photo/${this.album.id}`,
      })

      const payload = fileIds.map(id => ({ file_id: id }))

      try {
        const result = await postPhotoModule.createItem(payload)

        this.album = result.data
        this.startOption = result.data.start
        this.photos = result.data.photos
        this.albumName = result.data.name
        this.setPhotoIds(result.data.photos)
      } catch (e) {
        this.batchError =
          e?.response?.data?.message || 'Ошибка добавления фото в альбом'
      }
    },
    async uploadBatch() {
      if (!this.batchFiles.length) return

      const CHUNK_SIZE = this.batchChunkSize || 20

      this.batchUploading = true
      this.batchProgress = 0
      this.batchError = null

      try {
        const total = this.batchFiles.length
        let uploadedCount = 0

        // бьём на чанки
        for (let start = 0; start < total; start += CHUNK_SIZE) {
          const chunk = this.batchFiles.slice(start, start + CHUNK_SIZE)

          const form = new FormData()

          // ✅ "массив" бинарников в multipart = повторяющийся ключ
          chunk.forEach(f => {
            form.append('files', f, f.name)
          })

          // ✅ lastModified одно значение (текущая дата)
          form.append('lastModified', String(Date.now()))

          const res = await axios.post(
            `${config.host}/file/upload-batch`,
            form,
            {
              headers: { 'Content-Type': 'multipart/form-data' },
            },
          )

          const uploaded = Array.isArray(res.data)
            ? res.data
            : res.data?.files || []
          const ids = uploaded.map(x => x?.id).filter(Boolean)

          // не добавляем дубли
          const newIds = ids.filter(id => !this.photoIds.includes(id))

          // одним запросом добавляем в альбом
          await this.postPhotoBatch(newIds)

          // прогресс по чанкам
          uploadedCount += chunk.length
          this.batchProgress = Math.round((uploadedCount * 100) / total)
        }

        this.clearBatch()
        this.resetScroll()
        this.filesBeforeUpload = false
      } catch (e) {
        this.batchError =
          e?.response?.data?.message || 'Ошибка пакетной загрузки'
      } finally {
        this.batchUploading = false
      }
    },
    onBatchPick(evt) {
      const files = Array.from(evt?.target?.files || [])
      this.addBatchFiles(files)
      this.filesBeforeUpload = files.length !== 0
    },

    onBatchDrop(evt) {
      this.batchDragOver = false
      const files = Array.from(evt?.dataTransfer?.files || [])
      this.addBatchFiles(files)
      this.filesBeforeUpload = files.length !== 0
    },

    addBatchFiles(files) {
      this.batchError = null
      const onlyImages = files.filter(
        f => f && f.type && f.type.startsWith('image/'),
      )

      const key = f => `${f.name}_${f.lastModified}_${f.size}`
      const existing = new Set(this.batchFiles.map(key))

      const prepared = onlyImages
        .filter(f => !existing.has(key(f)))
        .map(f => {
          f.__preview = URL.createObjectURL(f)
          return f
        })

      this.batchFiles = [...this.batchFiles, ...prepared]
    },

    removeBatchFile(i) {
      const f = this.batchFiles[i]
      if (f?.__preview) URL.revokeObjectURL(f.__preview)
      this.batchFiles.splice(i, 1)
      this.filesBeforeUpload = this.batchFiles.length !== 0
    },

    clearBatch() {
      this.batchFiles.forEach(f => {
        if (f?.__preview) URL.revokeObjectURL(f.__preview)
      })
      this.batchFiles = []
      this.batchProgress = 0
      this.batchError = null
      this.filesBeforeUpload = false
    },

    prettyBytes(bytes) {
      if (!Number.isFinite(bytes)) return ''
      const units = ['B', 'KB', 'MB', 'GB']
      let i = 0
      let v = bytes
      while (v >= 1024 && i < units.length - 1) {
        v /= 1024
        i++
      }
      return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`
    },
  },
}
</script>

<style scoped lang="scss">
.save-button {
  position: fixed !important;
  bottom: 30px;
  right: 15px;
  z-index: 1000;
  margin-top: 20px !important;
}
.tagWrap {
  margin: 20px;
  margin-bottom: 0px;
  display: flex;
  gap: 10px;
}
.cursor {
  cursor: pointer;
}

.disabled-icon {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.disabled-container {
  opacity: 0.6;
  pointer-events: none;
  user-select: none;
}

.flex-column {
  display: flex;
  flex-direction: column;
}
.flex-row {
  display: flex;
  gap: 20px;
  align-items: center;
}

.tag-suggest-wrap {
  position: relative;
  flex: 1 1 auto;
}

.tag-suggest-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  width: 100%;
  z-index: 2000;
  background: #fff;
  border: 1px solid rgba(34, 41, 47, 0.15);
  border-radius: 0.358rem;
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.12);
  max-height: 220px;
  overflow: auto;
  padding: 6px 0;
}

.tag-suggest-item {
  width: 100%;
  border: 0;
  background: transparent;
  padding: 8px 12px;
  text-align: left;
  color: #5e5873;
  cursor: pointer;
}

.tag-suggest-item:hover,
.tag-suggest-item:focus {
  background: rgba(115, 103, 240, 0.12);
  color: #7367f0;
  outline: none;
}

.dark-layout .tag-suggest-dropdown {
  background: #161d31;
  border-color: rgba(255, 255, 255, 0.12);
}

.dark-layout .tag-suggest-item {
  color: #d0d2d6;
}

.dark-layout .tag-suggest-item:hover,
.dark-layout .tag-suggest-item:focus {
  background: rgba(115, 103, 240, 0.22);
  color: #fff;
}
.upload-wrap {
  margin-bottom: 20px;
}

.container {
  display: flex;
  gap: 20px;
  flex-direction: column;
}

.scroll {
  overflow: auto;
  height: 510px;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}
.images-container {
  padding: 10px;
  display: flex;
  flex-direction: row;
  gap: 40px;
  flex-wrap: wrap;
}

.hidden-container {
  display: none;
}

.dark-layout .batch-dropzone {
  background: #161d31;
}

.dark-layout .batch-item {
  background: #161d31;
}

.batch-dropzone {
  border: 1px dashed #c7c7c7;
  border-radius: 12px;
  padding: 14px;
  background: #fff;
}

.batch-dropzone.is-dragover {
  background: #161d31;
  border-color: #5b8def;
}

.batch-dropzone__title {
  font-weight: 600;
  margin-bottom: 10px;
}

.batch-dropzone__actions {
  margin-bottom: 12px;
}

.batch-list__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.batch-list__buttons {
  display: flex;
  gap: 8px;
}

.batch-list__items {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 300px;
  overflow-y: scroll;
}

.batch-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px;
  border: 1px solid #eee;
  border-radius: 10px;
  background: #161d31;
}

.batch-item__preview {
  width: 46px;
  height: 46px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #f0f0f0;
}

.batch-item__meta {
  flex: 1;
  min-width: 0;
}

.batch-item__name {
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.batch-item__sub {
  font-size: 12px;
  opacity: 0.75;
}

.batch-progress-text {
  font-size: 12px;
  margin-top: 6px;
  opacity: 0.8;
}

.batch-error {
  margin-top: 10px;
  color: #b42318;
  background: #161d31;
  border: 1px solid #ffd6d6;
  padding: 8px 10px;
  border-radius: 10px;
}
</style>
<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>
